// src/components/PhotoUploadDialog.jsx
import { useState, useEffect, useRef } from 'react';
import { Upload, Loader2, X, Image, ChevronDown, ChevronUp, AlertTriangle, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/lib/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { useBulkUploadPhotos } from '@/hooks/usePhotos';

export default function PhotoUploadDialog({ open, onOpenChange, onSuccess, preselectedGroupId = null }) {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [children, setChildren] = useState([]);
  const [expandedPhotos, setExpandedPhotos] = useState(new Set());
  const fileInputRef = useRef(null);

  const [uploadForm, setUploadForm] = useState({
    group_id: '',
    photos: [] // Array of { file: File, preview: string, caption: string, child_ids: Array }
  });
  const [uploadProgress, setUploadProgress] = useState(null); // { completed, total, phase } when uploading
  const [consentErrorDialog, setConsentErrorDialog] = useState({
    open: false,
    childrenWithoutConsent: []
  });

  const bulkUploadMutation = useBulkUploadPhotos();

  // Load groups and children when dialog opens
  useEffect(() => {
    if (open && userProfile) {
      loadData();
    }
  }, [open, userProfile]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setUploadForm({
        group_id: '',
        photos: []
      });
      setExpandedPhotos(new Set());
      // Clean up object URLs
      uploadForm.photos.forEach(photo => {
        if (photo.preview) {
          URL.revokeObjectURL(photo.preview);
        }
      });
    }
  }, [open]);

  // Set preselected group when groups load
  useEffect(() => {
    if (groups.length > 0 && !uploadForm.group_id) {
      const groupId = preselectedGroupId && groups.some(g => g.id === preselectedGroupId)
        ? preselectedGroupId
        : groups[0]?.id;
      setUploadForm(prev => ({ ...prev, group_id: groupId }));
    }
  }, [groups, preselectedGroupId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const branchId = userProfile.current_branch_id || userProfile.branch_ids?.[0];
      if (!branchId) {
        setGroups([]);
        setChildren([]);
        return;
      }

      // Get groups where teacher is assigned
      const { data: allGroups, error: groupsError } = await supabase
        .from('groups')
        .select('*')
        .eq('branch_id', branchId);

      if (groupsError) throw groupsError;

      // Filter groups where teacher is main or secondary
      const teacherGroups = allGroups?.filter(g => {
        const isMainTeacher = g.main_teacher_id === userProfile.id;
        const isInGroupTeacherIds = g.teacher_ids?.includes(userProfile.id);
        const isInUserGroupIds = userProfile.group_ids?.includes(g.id);
        return isMainTeacher || isInGroupTeacherIds || isInUserGroupIds;
      }) || [];

      setGroups(teacherGroups);

      // Load children for these groups
      const groupIds = teacherGroups.map(g => g.id);
      if (groupIds.length > 0) {
        const { data: childrenData, error: childrenError } = await supabase
          .from('children')
          .select('*')
          .in('group_id', groupIds)
          .eq('status', 'active');

        if (childrenError) throw childrenError;
        setChildren(childrenData || []);
      }
    } catch (e) {
      logger.error('Failed to load data:', e);
      toast.error('Nie udalo sie zaladowac danych');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name}: Nieprawidłowy typ pliku`);
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name}: Obraz musi być mniejszy niż 10MB`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
    }

    if (validFiles.length > 0) {
      const newPhotos = validFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        caption: '',
        child_ids: []
      }));

      setUploadForm(prev => ({
        ...prev,
        photos: [...prev.photos, ...newPhotos]
      }));

      // Expand all new photos by default
      const newIndices = new Set();
      const startIndex = uploadForm.photos.length;
      for (let i = 0; i < newPhotos.length; i++) {
        newIndices.add(startIndex + i);
      }
      setExpandedPhotos(prev => new Set([...prev, ...newIndices]));
    }

    // Reset input to allow selecting the same files again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    const photo = uploadForm.photos[index];
    if (photo.preview) {
      URL.revokeObjectURL(photo.preview);
    }
    
    setUploadForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
    
    setExpandedPhotos(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      // Adjust indices for photos after the removed one
      const adjusted = new Set();
      newSet.forEach(idx => {
        if (idx > index) {
          adjusted.add(idx - 1);
        } else {
          adjusted.add(idx);
        }
      });
      return adjusted;
    });
  };

  const updatePhotoMetadata = (index, updates) => {
    setUploadForm(prev => ({
      ...prev,
      photos: prev.photos.map((photo, i) => 
        i === index ? { ...photo, ...updates } : photo
      )
    }));
  };

  const togglePhotoExpanded = (index) => {
    setExpandedPhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleUpload = async () => {
    if (uploadForm.photos.length === 0 || !uploadForm.group_id) {
      toast.error('Prosze wybrac zdjecia i grupe');
      return;
    }

    // Check if all photos have children tagged (required)
    const photosWithoutTags = uploadForm.photos.filter(p => !p.child_ids || p.child_ids.length === 0);
    if (photosWithoutTags.length > 0) {
      toast.error('Musisz oznaczyć wszystkie dzieci widoczne na zdjęciach przed przesłaniem');
      return;
    }

    setUploading(true);
    setUploadProgress({ completed: 0, total: uploadForm.photos.length, phase: 'uploading' });
    try {
      const branchId = userProfile.current_branch_id || userProfile.branch_ids?.[0];
      
      // Prepare photos for upload
      const photosToUpload = uploadForm.photos.map(photo => ({
        file: photo.file,
        caption: photo.caption || null,
        childIds: photo.child_ids || []
      }));

      // Use bulk upload mutation with progress and retries
      const result = await bulkUploadMutation.mutateAsync({
        photos: photosToUpload,
        groupId: uploadForm.group_id,
        branchId,
        onProgress: (completed, total, phase) => {
          setUploadProgress({ completed, total, phase });
        }
      });

      // Clean up object URLs
      uploadForm.photos.forEach(photo => {
        if (photo.preview) {
          URL.revokeObjectURL(photo.preview);
        }
      });

      toast.success(`Przeslano ${result.successCount} z ${result.total} zdjec`);
      onOpenChange(false);
      setUploadForm({
        group_id: groups[0]?.id || '',
        photos: []
      });
      setExpandedPhotos(new Set());
      onSuccess?.();
    } catch (error) {
      // Handle consent validation error (expected flow - dialog handles it)
      const errorMessage = error?.message || error?.toString() || '';
      if (errorMessage.includes('CONSENT_VALIDATION_FAILED:')) {
        try {
          const jsonPart = errorMessage.split('CONSENT_VALIDATION_FAILED:')[1];
          if (jsonPart) {
            const childrenData = JSON.parse(jsonPart);
            setConsentErrorDialog({
              open: true,
              childrenWithoutConsent: Array.isArray(childrenData) ? childrenData : [childrenData]
            });
            return;
          }
        } catch (parseError) {
          logger.error('Failed to parse consent error:', parseError, { errorMessage });
          toast.error('Niektóre dzieci nie mają zgody na publikację wizerunku. Usuń oznaczenia tych dzieci i spróbuj ponownie.');
        }
      } else {
        logger.error('Failed to upload:', error);
        toast.error('Nie udalo sie przeslac zdjec: ' + (errorMessage || 'Nieznany błąd'));
      }
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const groupChildren = uploadForm.group_id
    ? children.filter(c => c.group_id === uploadForm.group_id)
    : [];

  // Batch operations
  const applyCaptionToAll = (caption) => {
    setUploadForm(prev => ({
      ...prev,
      photos: prev.photos.map(photo => ({ ...photo, caption }))
    }));
  };

  const applyChildTagsToAll = (childIds) => {
    setUploadForm(prev => ({
      ...prev,
      photos: prev.photos.map(photo => ({ ...photo, child_ids: childIds }))
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Dodaj zdjecia</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            Nie jestes przypisany do zadnej grupy.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Group Selection */}
            <div>
              <Label>Grupa</Label>
              <Select
                value={uploadForm.group_id}
                onValueChange={(v) => setUploadForm(p => ({ 
                  ...p, 
                  group_id: v, 
                  photos: p.photos.map(photo => ({ ...photo, child_ids: [] }))
                }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Wybierz grupe" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map(group => (
                    <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* File Upload Area */}
            <div>
              <Label>Zdjecia ({uploadForm.photos.length})</Label>
              <div className="mt-2">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-colors">
                  <Image className="w-8 h-8 text-slate-400 mb-2" />
                  <span className="text-sm text-slate-500">Kliknij aby wybrac zdjecia (mozna wybrac wiele)</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </label>
              </div>
            </div>

            {/* Photos List with Individual Metadata */}
            {uploadForm.photos.length > 0 && (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {uploadForm.photos.map((photo, index) => {
                  const isExpanded = expandedPhotos.has(index);
                  
                  return (
                    <div key={index} className="border rounded-lg p-3 space-y-2">
                      {/* Photo Preview Header */}
                      <div className="flex items-center gap-3">
                        <div className="relative flex-shrink-0">
                          <img
                            src={photo.preview}
                            alt={`Podglad ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                          <Button
                            variant="secondary"
                            size="icon"
                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full"
                            onClick={() => removePhoto(index)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{photo.file.name}</p>
                          <p className="text-xs text-slate-500">
                            {(photo.file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => togglePhotoExpanded(index)}
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      </div>

                      {/* Expanded Metadata */}
                      {isExpanded && (
                        <div className="space-y-3 pt-2 border-t">
                          {/* Caption */}
                          <div>
                            <Label htmlFor={`caption-${index}`} className="text-xs">Opis (opcjonalnie)</Label>
                            <Textarea
                              id={`caption-${index}`}
                              value={photo.caption}
                              onChange={(e) => updatePhotoMetadata(index, { caption: e.target.value })}
                              placeholder="Dodaj opis..."
                              rows={2}
                              className="mt-1 text-sm"
                            />
                          </div>

                          {/* Tag Children - REQUIRED */}
                          {groupChildren.length > 0 && (
                            <div>
                              <Label className="text-xs">
                                Oznacz dzieci <span className="text-red-500">*</span>
                                {photo.child_ids.length === 0 && (
                                  <span className="text-red-500 text-xs ml-2">(wymagane)</span>
                                )}
                              </Label>
                              <div className="max-h-24 overflow-y-auto space-y-1.5 p-2 border rounded-lg mt-1">
                                {groupChildren.map(child => (
                                  <label key={child.id} className="flex items-center gap-2 cursor-pointer">
                                    <Checkbox
                                      checked={photo.child_ids.includes(child.id)}
                                      onCheckedChange={(checked) => {
                                        const newChildIds = checked
                                          ? [...photo.child_ids, child.id]
                                          : photo.child_ids.filter(id => id !== child.id);
                                        updatePhotoMetadata(index, { child_ids: newChildIds });
                                      }}
                                    />
                                    <span className="text-xs">{child.first_name} {child.last_name}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                          {groupChildren.length === 0 && (
                            <Alert>
                              <AlertTriangle className="h-4 w-4" />
                              <AlertDescription className="text-xs">
                                Brak dzieci w tej grupie. Nie możesz przesłać zdjęć bez oznaczenia dzieci.
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Batch Operations */}
            {uploadForm.photos.length > 1 && uploadForm.group_id && (
              <div className="border-t pt-3 space-y-2">
                <Label className="text-xs text-slate-500">Operacje na wszystkich zdjeciach:</Label>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const caption = prompt('Wprowadz opis dla wszystkich zdjec:');
                      if (caption !== null) {
                        applyCaptionToAll(caption);
                      }
                    }}
                  >
                    Zastosuj opis do wszystkich
                  </Button>
                  {groupChildren.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const selected = uploadForm.photos[0]?.child_ids || [];
                        const allSelected = uploadForm.photos.every(p => 
                          p.child_ids.length === selected.length && 
                          p.child_ids.every(id => selected.includes(id))
                        );
                        applyChildTagsToAll(allSelected ? [] : selected);
                      }}
                    >
                      Zastosuj oznaczenia do wszystkich
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Upload progress: progress bar + phase (like bulk import) */}
        {uploading && uploadProgress && uploadProgress.total > 0 && (
          <div className="flex flex-col items-center py-4 px-4 rounded-xl bg-slate-50 border border-slate-200 animate-in fade-in duration-200">
            <div className="relative">
              <Loader2 className="w-12 h-12 text-violet-600 animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Camera className="w-6 h-6 text-violet-600" />
              </div>
            </div>
            <p className="mt-3 text-sm font-semibold text-slate-800">
              {uploadProgress.phase === 'saving'
                ? 'Zapisywanie w galerii...'
                : 'Przesyłanie zdjęć...'}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {uploadProgress.completed} / {uploadProgress.total} zdjęć
            </p>
            <div className="w-full max-w-xs mt-3 space-y-1.5">
              <Progress
                value={uploadProgress.total ? (uploadProgress.completed / uploadProgress.total) * 100 : 0}
                className="h-2.5"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>{Math.round(uploadProgress.total ? (uploadProgress.completed / uploadProgress.total) * 100 : 0)}%</span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Anuluj
          </Button>
          <Button
            onClick={handleUpload}
            disabled={uploading || loading || uploadForm.photos.length === 0 || !uploadForm.group_id}
            className="bg-gradient-to-r from-purple-500 to-violet-600"
          >
            {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Upload className="w-4 h-4 mr-2" />
            Przeslij {uploadForm.photos.length > 0 && `(${uploadForm.photos.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Consent Error Dialog */}
      <Dialog open={consentErrorDialog.open} onOpenChange={(open) => setConsentErrorDialog({ open, childrenWithoutConsent: [] })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Brak zgody na publikację wizerunku
            </DialogTitle>
            <DialogDescription>
              Niektóre dzieci nie mają zgody na publikację wizerunku. Aby przesłać zdjęcie, musisz wytnąć/zasłonić wizerunek odpowiedniego dziecka i prześlij zdjęcie ponownie bez oznaczania tego dziecka w aplikacji.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm font-medium">Dzieci wymagające maskowania:</p>
            <ul className="list-disc list-inside space-y-1 text-sm text-slate-600">
              {consentErrorDialog.childrenWithoutConsent.map((child, idx) => (
                <li key={idx}>{child.childName || child.childId}</li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button onClick={() => setConsentErrorDialog({ open: false, childrenWithoutConsent: [] })}>
              Rozumiem
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
